import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { api, setToken as setApiToken } from '../services/api';

export const AuthContext = createContext({
  user: null,
  token: null,
  loading: true,
  login: async (_email, _password) => {},
  logout: () => {},
});

function safeLocalStorageGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeLocalStorageSet(key, val) {
  try { window.localStorage.setItem(key, val); } catch {}
}
function safeLocalStorageRemove(key) {
  try { window.localStorage.removeItem(key); } catch {}
}

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // hydrate from storage or cookie-based session
  useEffect(() => {
    const t = safeLocalStorageGet('jwt');
    if (t) {
      setToken(t);
      setApiToken(t);
      api.me().then(async (u) => {
        if (u && !u.error) {
          setUser(u.item || u);
          return;
        }
        if (u?.error === 'unauthorized') {
          // Stored JWT may be stale; clear and retry cookie session once.
          safeLocalStorageRemove('jwt');
          setToken(null);
          setApiToken(null);
          try {
            const retry = await api.me();
            if (retry && !retry.error) setUser(retry.item || retry);
          } catch {}
        }
      }).finally(() => setLoading(false));
    } else {
      api.me().then((u) => {
        if (u && !u.error) setUser(u.item || u);
      }).finally(() => setLoading(false));
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.login({ email, password });
    if (res && res.token) {
      setToken(res.token);
      setApiToken(res.token);
      safeLocalStorageSet('jwt', res.token);
      // Bust session-scoped caches on new login
      try { window.sessionStorage.removeItem('amplifyConfigCache'); } catch {}
      const me = await api.me();
      if (me && !me.error) setUser(me.item || me);
      return { ok: true };
    }
    return { ok: false, error: res?.error || 'Login failed' };
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setApiToken(null);
    safeLocalStorageRemove('jwt');
    // Bust session-scoped caches on logout
    try { window.sessionStorage.removeItem('amplifyConfigCache'); } catch {}
  }, []);

  const value = useMemo(() => ({ user, token, loading, login, logout }), [user, token, loading, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
